// @ts-nocheck

import classNames from 'classnames';
import * as R from 'ramda';
import React, { Component } from 'react';
import { SUMMARY_PAGE_STYLE_VARIANT } from '../../../../main/screens/ScreenNewSummary/newSummaryConstants';
import type { Filter } from '../../../../main/types/druidTypes';
import { OPERATORS, RESTRICTED_OPERATORS, getListOfDimensionsAndValues } from '../../../support/utils/druid/nativeQueries';
import { addFilter, updateFilter } from '../../../support/utils/druid/updateQuery';
import { Dropdown } from '../../core';
import { getUnselectedFilterTypes, sortByLabel, toSelectValue } from './utils';
const STATIC_PATH = '/static/images/editor/';
type Props = {
  i: number;
  row: Record<string, any>;
  starter: string;
  filter?: Filter;
  requiredFilter?: Filter;
  filter_types: Record<string, any>[];
  filter_options: Record<string, any>;
  customLabelMap?: Record<string, any>;
  displayFilters: Record<string, any>[];
  deleteFilterRow: (arg0: Record<string, any>) => void;
  updateQuery: (arg0: Record<string, any>) => void;
  fetchFilters: (...args: Array<any>) => any;
  setEmptyField: (arg0: boolean) => void;
  updateBlockedEventIndicator?: (arg0: string) => void;
  dimensions?: Array<string>;
  styleVariant?: string;
};
type State = {
  loading: boolean;
};
export function getComparatorValue(value: string = OPERATORS.EQUALS) {
  const operator = value;
  return {
    value: operator,
    label: operator,
  };
}

class FilterRow extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      loading: false,
    };

    // Don't need to fetch filters for requiredFilter since it's a static value
    if (props.row.dimension) {
      if (props.requiredFilter && props.requiredFilter.dimension && props.row.dimension !== props.requiredFilter.dimension) {
        props.fetchFilters(props.row.dimension);
      }
    }
  }

  /**
   * Adds updates the values for a given query dimension
   * @param {Object} row   - object representing the filter row
   * @param {Array} values - list of values to add to the filter dimension
   */
  updateDimensionValues = (row: Record<string, any>, values: Record<string, any>[]) => {
    const { filter, updateBlockedEventIndicator } = this.props;
    this.props.updateQuery({
      filter: updateFilter(
        filter,
        row,
        null,
        null,
        values.map((option) => option.value)
      ),
      filters: null,
    });
    updateBlockedEventIndicator && updateBlockedEventIndicator('none');
  };

  /**
   * Adds a filter dimension to query or adds an empty row
   * @param {Object} row - object representing the filter row
   * @param {Object} previousRow - object representing the previous filter row
   * @param {string} value - value to add as the filter dimension
   */
  updateDimensionKey = (row: Record<string, any>, previousRow: Record<string, any>, value: string) => {
    const { filter, updateBlockedEventIndicator, dimensions, updateQuery, setEmptyField } = this.props;

    if (value === null) {
      setEmptyField(true);
    } else {
      setEmptyField(false);
    }

    // [EG-3109] Add fraud code / fraud name to compare by if a filter is selected on one of these fields
    // this is necessary for the UI-level filtering to work (see filterTableRows in fraudUtils.js)
    if (value === 'fraud_codes' || value === 'fraud_names') {
      if (dimensions && !dimensions.includes(value)) {
        updateQuery({
          dimensions: [...dimensions, value],
        });
      }
    }

    if (row.dimension) {
      updateQuery({
        filter: updateFilter(filter, row, value, OPERATORS.EQUALS, ['']),
        filters: null,
      });
    } else if (value) {
      updateQuery({
        filter: addFilter(filter, value, previousRow),
        filters: null,
      });
    }

    updateBlockedEventIndicator && updateBlockedEventIndicator('none');
  };

  fetchFilters = (row: Record<string, any>) => {
    if (row.dimension && !this.state.loading) {
      this.setState({
        loading: true,
      });
      this.props.fetchFilters(row.dimension).finally(() => {
        this.setState({
          loading: false,
        });
      });
    }
  };

  updateComparator = (row: Record<string, any>, value: string) => {
    const { filter, updateBlockedEventIndicator } = this.props;
    this.props.updateQuery({
      filter: updateFilter(filter, row, null, value, null),
    });
    updateBlockedEventIndicator && updateBlockedEventIndicator('none');
  };

  /**
   * Custom filter function for react-select to remove already select options
   * @param (Array) options - react-select options
   * @param {string} string - filterOptions prop overrides searchable by default. This adds it back
   * @return {Array} filtered search options
   */
  filterOptions = (options: Record<string, any>[], string: string) => {
    const used = R.keys(getListOfDimensionsAndValues(this.props.filter));
    const filtered = R.reject((filter) => {
      const val = filter.value;
      return R.contains(val, used);
    }, options);

    // react-select filterOptions prop overrides searchable by default
    // this adds it back if a search term is entered
    if (string) {
      return R.filter((filter) => {
        const val = filter.value;
        const label = filter.label;
        return val.includes(string) || label.includes(string);
      }, filtered);
    }

    return filtered;
  };

  _deleteFilterRow = (row: Record<string, any>) => {
    const { deleteFilterRow, updateBlockedEventIndicator } = this.props;
    deleteFilterRow(row);
    updateBlockedEventIndicator && updateBlockedEventIndicator('none');
  };

  render() {
    const { i, row, starter, filter_types, filter_options, customLabelMap, displayFilters, styleVariant } = this.props;
    // [EG-3109] only show equals and does not equal operators for fraud_names and fraud_codes filters
    const dimensionIsFraudDimension = row.dimension === 'fraud_names' || row.dimension === 'fraud_codes';
    const operators = dimensionIsFraudDimension ? RESTRICTED_OPERATORS : OPERATORS;
    const unselectedFilterTypes = getUnselectedFilterTypes(displayFilters, filter_types);
    return (
      <div key={i} data-testid={`filter__row-${i}`}>
        <div
          className={classNames('analytics-filters__row', {
            'required-in-modal': row.required && styleVariant === SUMMARY_PAGE_STYLE_VARIANT,
          })}
        >
          <span className="analytics-filters__starter">{starter}</span>
          <Dropdown
            testid={`filter-key__row-${i}`}
            disabled={row.required}
            clearable={false}
            options={filter_types && sortByLabel(unselectedFilterTypes)}
            filterOptions={this.filterOptions}
            value={row.dimension ? toSelectValue(row.dimension, customLabelMap) : null}
            onChange={(e) => this.updateDimensionKey(row, displayFilters[i - 1] || {}, e && e.value)}
            className={classNames('source-row__select', {
              'in-modal': styleVariant === SUMMARY_PAGE_STYLE_VARIANT,
            })}
          />
          <Dropdown
            outerClassName="h_miw-150 h_mstart-10"
            testid={`filter-comparator__row-${i}`}
            disabled={row.required}
            clearable={false}
            options={Object.values(operators).map((value) => ({
              label: value,
              value,
            }))}
            value={getComparatorValue(row.comparator || operators.EQUALS)}
            onChange={(e) => this.updateComparator(row, e && e.value)}
          />
          <Dropdown
            testid={`filter-value__row-${i}`}
            outerClassName="h_w-50 h_mstart-10"
            disabled={row.required}
            multi={true}
            options={filter_options[row.dimension] && sortByLabel(filter_options[row.dimension])}
            value={
              row.values
                ? row.values.filter((v) => v).map((v) => toSelectValue(v, customLabelMap, false, row.dimension))
                : null
            }
            onChange={(e) => this.updateDimensionValues(row, e)}
            // biome-ignore lint/correctness/noUnusedVariables: ignored using `--suppress`
            onOpen={(e) => this.fetchFilters(row)}
            creatable={true}
            isLoading={this.state.loading}
          />

          {row.required ? null : (
            <img
              data-testid={`remove-filter__row-${i}`}
              className="remove-filter-btn"
              onClick={() => this._deleteFilterRow(row)}
              src={STATIC_PATH + 'ic_remove_button.svg'}
            />
          )}
        </div>
      </div>
    );
  }
}

export default FilterRow;
